/**
 * Tabs module for the Plans section
 * Exports: tabs(), currency()
 *
 * Level and currency together pick the checkout link. Every buy button carries
 * one data-href-{level}-{currency} attribute per combination, so switching
 * either control just repoints the href — see vfit_checkout_attrs() in
 * template-parts/pages/home/section-plans.php.
 */

// ─── Shared selection ─────────────────────────────────────────────────────────

const selection = { level: 'basics', currency: 'usd' };

// Repoint every buy button at the offer for the current level and currency.
function applyCheckoutLinks() {
  const links = document.querySelectorAll('[data-checkout]');
  if (!links || links.length === 0) return;

  const attr = `data-href-${selection.level}-${selection.currency}`;

  links.forEach((el) => {
    const href = el.getAttribute(attr);

    if (href) {
      el.setAttribute('href', href);
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
      el.removeAttribute('aria-disabled');
      el.style.opacity = '';
      el.style.pointerEvents = '';
      return;
    }

    // No offer for this combination yet — keep the button inert rather than
    // sending the customer to the wrong level.
    el.setAttribute('href', '#plans');
    el.removeAttribute('target');
    el.setAttribute('aria-disabled', 'true');
    el.style.opacity = '.45';
    el.style.pointerEvents = 'none';
  });
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

export function tabs() {
  const buttons = document.querySelectorAll('.plans-tabs__btn');
  const panels = document.querySelectorAll('.plans-tabs__panel');

  if (!buttons || buttons.length === 0) return;
  if (!panels || panels.length === 0) return;

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const tabIndex = btn.getAttribute('data-tab');

      // Remove is-active from all buttons
      buttons.forEach((b) => b.classList.remove('is-active'));

      // Remove is-active from all panels
      panels.forEach((p) => p.classList.remove('is-active'));

      // Add is-active to clicked button
      btn.classList.add('is-active');

      // Switch the checkout links over to this level
      const level = btn.getAttribute('data-level');
      if (level) {
        selection.level = level;
        applyCheckoutLinks();
      }

      // Add is-active to matching panel
      const targetPanel = document.querySelector(`.plans-tabs__panel[data-panel="${tabIndex}"]`);
      if (!targetPanel) return;
      targetPanel.classList.add('is-active');
    });
  });

  // Adopt whichever tab is marked active in the markup
  const active = document.querySelector('.plans-tabs__btn.is-active[data-level]');
  if (active) selection.level = active.getAttribute('data-level');
  applyCheckoutLinks();
}

// ─── Currency ─────────────────────────────────────────────────────────────────

// currency function — handles custom dropdown behavior and price updates
export function currency() {
  const trigger = document.querySelector('.currency-selector__trigger');
  const list = document.querySelector('.currency-selector__list');
  const options = document.querySelectorAll('.currency-selector__option');
  const valueDisplay = document.querySelector('.currency-selector__value');

  if (!trigger) return;
  if (!list) return;
  if (!options || options.length === 0) return;
  if (!valueDisplay) return;

  // Toggle dropdown open/close
  trigger.addEventListener('click', () => {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', !isExpanded);
    list.hidden = isExpanded;
  });

  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!trigger.contains(e.target) && !list.contains(e.target)) {
      trigger.setAttribute('aria-expanded', 'false');
      list.hidden = true;
    }
  });

  // Handle option selection
  options.forEach(option => {
    option.addEventListener('click', () => {
      const selectedValue = option.getAttribute('data-value'); // 'usd' or 'eur'
      const selectedText = option.textContent; // 'USD ($)' or 'EUR (€)'

      // Update trigger display
      valueDisplay.textContent = selectedText;

      // Close dropdown
      trigger.setAttribute('aria-expanded', 'false');
      list.hidden = true;

      // Update all prices on the page
      updatePrices(selectedValue);
    });
  });

  // Update prices based on selected currency
  function updatePrices(currency) {
    const prefix = currency === 'usd' ? '$' : '€';
    const dataAttr = `data-${currency}`;

    // Update all .price elements
    const prices = document.querySelectorAll('.price');
    if (prices && prices.length > 0) {
      prices.forEach(el => {
        const value = el.getAttribute(dataAttr);
        if (value) el.textContent = prefix + value;
      });
    }

    // Update all .price-bulk elements
    const bulkPrices = document.querySelectorAll('.price-bulk');
    if (bulkPrices && bulkPrices.length > 0) {
      bulkPrices.forEach(el => {
        const value = el.getAttribute(dataAttr);
        if (value) {
          const text = el.textContent;
          // Replace only the price part, keep "12 weeks Pass for"
          el.textContent = text.replace(/[\$€][\d.]+/, prefix + value);
        }
      });
    }

    // Update all .price-weekly elements
    const weeklyPrices = document.querySelectorAll('.price-weekly');
    if (weeklyPrices && weeklyPrices.length > 0) {
      weeklyPrices.forEach(el => {
        const value = el.getAttribute(dataAttr);
        if (value) el.textContent = prefix + value;
      });
    }

    // Update all checkout links, keeping the level the customer picked
    selection.currency = currency;
    applyCheckoutLinks();
  }
}
